import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Search } from 'lucide-react';
import { api } from './api';
import { errorMessage } from './errors';
import PageContainer from './kit/PageContainer';
import { formatDate } from './kit/time';
import { Input } from '../components/ui/input';

// Read-only view of the guideline knowledge base Audrey answers from (ticket 197).
// Uses the existing search route; no editing.
const AGENCIES = [
  { key: 'fannie', label: 'Fannie Mae' },
  { key: 'freddie', label: 'Freddie Mac' },
  { key: 'fha', label: 'FHA' },
  { key: 'va', label: 'VA' },
  { key: 'usda', label: 'USDA' },
  { key: 're_generic', label: 'Real estate' },
];

export default function GuidelinesBrowserPage() {
  const [query, setQuery] = useState('');
  const [agency, setAgency] = useState('');
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    const timer = setTimeout(async () => {
      try {
        const params = { limit: 500 };
        if (query.trim()) params.q = query.trim();
        if (agency) params.agency = agency;
        const { data } = await api.get('/guidelines/search', { params });
        if (!live) return;
        setEntries(data.results || []);
        setError('');
      } catch (e) {
        if (live) setError(errorMessage(e, 'Could not load the guidelines'));
      }
    }, query ? 250 : 0);
    return () => { live = false; clearTimeout(timer); };
  }, [query, agency]);

  const groups = AGENCIES
    .map((a) => ({ ...a, items: (entries || []).filter((e) => e.agency === a.key) }))
    .filter((g) => g.items.length > 0);

  return (
    <PageContainer
      title="Browse guidelines"
      subtitle="The agency guidelines Audrey answers from. Read only."
      actions={(
        <Link to="/portal/ai" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900" data-testid="guidelines-back">
          <ArrowLeft size={15} /> Back to Audrey
        </Link>
      )}
      data-testid="guidelines-browser"
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative md:w-96">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search section, title or text"
            className="pl-9"
            aria-label="Search guidelines"
            data-testid="guidelines-search"
          />
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Agency">
          {[{ key: '', label: 'All' }, ...AGENCIES].map((a) => (
            <button
              key={a.key || 'all'}
              type="button"
              onClick={() => setAgency(a.key)}
              aria-pressed={agency === a.key}
              data-testid={`guidelines-agency-${a.key || 'all'}`}
              className={`px-3 py-1.5 rounded-full text-sm border ${agency === a.key ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="text-sm text-red-600" role="alert">{error}</div>}
      {!error && entries === null && <div className="text-sm text-slate-500">Loading…</div>}
      {!error && entries && groups.length === 0 && (
        <div className="text-sm text-slate-500" data-testid="guidelines-empty">No guidelines match.</div>
      )}

      {groups.map((g) => (
        <section key={g.key} className="space-y-3" data-testid={`guidelines-group-${g.key}`}>
          <h2 className="font-serif text-xl font-semibold text-brand-navy">
            {g.label} <span className="text-sm font-normal text-slate-500">({g.items.length})</span>
          </h2>
          {g.items.map((e) => (
            <article key={e.id} className="bg-white border border-slate-200 rounded-xl p-4 space-y-2" data-testid="guideline-entry">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-mono text-sm font-semibold text-brand-navy">{e.section_id}</span>
                <h3 className="font-semibold text-slate-900">{e.section_title}</h3>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{e.content}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                {e.source_url && (
                  <a href={e.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline">
                    Source <ExternalLink size={12} />
                  </a>
                )}
                <span data-testid="guideline-checked">Last checked: {formatDate(e.source_checked_at) || 'not checked yet'}</span>
                <span data-testid="guideline-changed">Last changed: {formatDate(e.source_changed_at) || '—'}</span>
              </div>
            </article>
          ))}
        </section>
      ))}
    </PageContainer>
  );
}
